import { Skeleton } from "@mantine/core";
import { useTaxProAnalytics } from "@utils/hooks/useAnalytics.ts";
import { useStatusStore } from "@utils/stores/useStatusStore.ts";
import { icons, UIStatuses } from "../../OverviewStatuses/StatusConstants.tsx";
import { clientStatuses } from "../../../../../constants";
import FilterChipsGroup from "../FilterChipsGroup/FilterChipsGroup.tsx";
import FilterChip from "../FilterChip/FilterChip.tsx";
import "./StatusFilterChips.scss";

/**
 * Color mapping that follows clientStatuses exactly as defined in ClientStatus.scss
 */
const statusColorMap: Record<
  string,
  { backgroundColor: string; textColor: string }
> = {
  D6F0FF: { backgroundColor: "#D6F0FF", textColor: "#0077cc" }, // Quote Sent
  "6CBAF2": { backgroundColor: "#6CBAF2", textColor: "#00477a" }, // In Progress
  D7F7DC: { backgroundColor: "#D7F7DC", textColor: "#176e26" }, // Completed
  CECDF8: { backgroundColor: "#CECDF8", textColor: "#383794" }, // Ready To E-File
  F3E1A4: { backgroundColor: "#F3E1A4", textColor: "#a78a2e" }, // Waiting
  FDD3D0: { backgroundColor: "#FDD3D0", textColor: "#dc373a" }, // Signatures Needed
  D9D9D9: { backgroundColor: "#D9D9D9", textColor: "#444444" }, // Archived
  F9C599: { backgroundColor: "#F9C599", textColor: "#c15800" }, // New
  "5f7f64": { backgroundColor: "#0077cc", textColor: "#fff" }, // Ready To Go
};

/**
 * Get colors from clientStatuses using the exact color mapping
 */
const getStatusColors = (
  status: string
): { backgroundColor: string; textColor: string } => {
  const statusEntry = Object.values(clientStatuses).find(
    (item) => item.status === status
  );

  if (statusEntry && statusColorMap[statusEntry.class]) {
    return statusColorMap[statusEntry.class];
  }

  // Default colors for unknown status
  return { backgroundColor: "#6B7280", textColor: "#ffffff" };
};

const StatusFilterChips = () => {
  const { statuses, isLoadingAnalytics } = useTaxProAnalytics();
  const { clickedStatus, setClickedStatus } = useStatusStore();

  const handleStatusClick = (statusText: string, count: number) => {
    // Don't allow clicking if count is 0
    if (count === 0) return;
    setClickedStatus(clickedStatus === statusText ? null : statusText);
  };

  if (isLoadingAnalytics) {
    return (
      <FilterChipsGroup data-testid="status-filter-chips-loading">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} height={36} width={120} radius="xl" />
        ))}
      </FilterChipsGroup>
    );
  }

  return (
    <FilterChipsGroup data-testid="status-filter-chips">
      {UIStatuses.map((status) => {
        const statusIndex = UIStatuses.findIndex((s) => s.id === status.id);
        const IconComponent = icons[statusIndex];
        const isActive = clickedStatus === status.text;
        const count = statuses?.[status.text] || 0;
        const isDisabled = count === 0;
        const { backgroundColor, textColor } = getStatusColors(status.text);

        return (
          <FilterChip
            key={status.id}
            isActive={isActive}
            color={backgroundColor}
            icon={IconComponent}
            onChange={() => handleStatusClick(status.text, count)}
            disabled={isDisabled}
            data-testid={`status-chip-${status.text}`}
            customStyles={{
              label: {
                color: isActive ? textColor : "#333",
              },
            }}
          >
            {status.text} ({count})
          </FilterChip>
        );
      })}
    </FilterChipsGroup>
  );
};

export default StatusFilterChips;
